import React from "react";
import { Helmet } from "react-helmet";
import ProductCard from "../components/ProductCard";
import { FaStar, FaHeart, FaShoppingCart, FaHome } from "react-icons/fa";

function Home() {
  const products = [
    {
      id: 1,
      name: "Natural Sandstone Pavers",
      shortDescription:
        "Premium Australian sandstone pavers perfect for garden pathways and outdoor living spaces.",
      price: 89.99,
      rating: 5,
      reviews: 127,
      image: "/images/1.jpg",
    },
    {
      id: 2,
      name: "River Stone Collection",
      shortDescription:
        "Smooth river stones ideal for water features, garden beds, and decorative landscaping.",
      price: 64.99,
      rating: 5,
      reviews: 94,
      image: "/images/2.jpg",
    },
    {
      id: 3,
      name: "Granite Garden Boulders",
      shortDescription:
        "Large decorative granite boulders to create stunning focal points in your landscape design.",
      price: 149.99,
      rating: 5,
      reviews: 78,
      image: "/images/3.jpg",
    },
    {
      id: 4,
      name: "Limestone Wall Cladding",
      shortDescription:
        "Elegant limestone panels for exterior walls, garden features, and architectural accents.",
      price: 119.99,
      rating: 5,
      reviews: 112,
      image: "/images/4.jpg",
    },
    {
      id: 5,
      name: "Slate Stepping Stones",
      shortDescription:
        "Durable slate stones creating beautiful pathways through gardens and outdoor areas.",
      price: 54.99,
      rating: 5,
      reviews: 156,
      image: "/images/5.jpg",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Tanarela - Premium Landscape Design Materials Australia</title>
        <meta
          name="description"
          content="Discover premium natural stone products and landscape design materials. Transform your outdoor space with sandstone pavers, granite boulders, and more."
        />
        <meta
          property="og:title"
          content="Tanarela - Premium Landscape Design Materials"
        />
        <meta
          property="og:description"
          content="Premium natural stone products for stunning landscape designs in Australia"
        />
        <link rel="canonical" href="https://tanarela.com/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Store",
            name: "Tanarela",
            description:
              "Premium landscape design materials and natural stone products",
            url: "https://tanarela.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: "437D Harrison Gates",
              addressLocality: "Laurenmouth",
              addressRegion: "QLD",
              postalCode: "2266",
              addressCountry: "AU",
            },
            telephone: "+61485588246",
          })}
        </script>
      </Helmet>

      <section className="hero-section">
        <div className="hero-content">
          <h1>Transform Your Outdoor Space with Natural Elegance</h1>
          <p>
            Discover premium landscape design materials crafted by nature,
            curated by experts. Elevate your garden with our exclusive
            collection of natural stone products.
          </p>
          <a href="#products" className="hero-cta">
            Explore Our Collection
          </a>
        </div>
      </section>

      <section className="features-section">
        <div className="features-container">
          <div className="features-header">
            <h2>Why Choose Tanarela for Your Landscape</h2>
            <p>
              We provide exceptional natural stone materials that combine
              beauty, durability, and sustainability for your outdoor design
              projects.
            </p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <FaStar className="feature-icon" />
              <h3>Premium Quality Materials</h3>
              <p>
                Every stone is carefully selected and sourced from the finest
                quarries across Australia, ensuring superior quality and
                authentic natural beauty in every piece.
              </p>
            </div>

            <div className="feature-card">
              <FaHeart className="feature-icon" />
              <h3>Sustainable Sourcing</h3>
              <p>
                We are committed to environmental responsibility, working with
                suppliers who practice sustainable quarrying and respect natural
                ecosystems.
              </p>
            </div>

            <div className="feature-card">
              <FaShoppingCart className="feature-icon" />
              <h3>Expert Design Support</h3>
              <p>
                Our team of landscape design specialists provides professional
                guidance to help you select the perfect materials for your
                unique outdoor vision.
              </p>
            </div>

            <div className="feature-card">
              <FaHome className="feature-icon" />
              <h3>Australian Delivery</h3>
              <p>
                Fast and reliable delivery service throughout Australia, with
                careful handling to ensure your premium stone products arrive in
                perfect condition.
              </p>
            </div>
          </div>

          <div className="stats-container">
            <div className="stat-item">
              <div className="stat-number">2500+</div>
              <div className="stat-label">Happy Customers</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">15</div>
              <div className="stat-label">Years Experience</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">500+</div>
              <div className="stat-label">Premium Products</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Quality Guarantee</div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-products-section">
        <div className="about-products-container">
          <h2>Natural Stone Excellence for Australian Landscapes</h2>

          <div className="about-products-content">
            <div className="about-column">
              <h3>Crafted by Nature, Perfected by Experts</h3>
              <p>
                Our natural stone products represent the pinnacle of landscape
                design materials. Each piece is formed over millions of years
                through geological processes, creating unique textures, colors,
                and patterns that cannot be replicated by manufactured products.
              </p>
              <p>
                We source our materials from premium Australian quarries known
                for their exceptional stone quality. From the warm tones of
                sandstone to the dramatic character of granite, every product in
                our collection has been carefully selected to meet the highest
                standards of durability, aesthetic appeal, and environmental
                sustainability.
              </p>
            </div>

            <div className="about-column">
              <h3>Quality Assurance and Certification</h3>
              <p>
                At Tanarela, quality is not just a promise but a guarantee.
                Every stone product undergoes rigorous quality control processes
                to ensure it meets Australian standards for outdoor construction
                materials. Our products are tested for density, water
                absorption, frost resistance, and structural integrity.
              </p>
              <p>
                We hold certifications from leading industry bodies and work
                exclusively with quarries that maintain environmental management
                systems. This commitment ensures that when you choose Tanarela,
                you are investing in materials that will enhance your landscape
                for decades to come while supporting responsible environmental
                practices.
              </p>
            </div>
          </div>

          <div className="about-highlights">
            <div className="highlight-item">
              <h4>Heritage and Tradition</h4>
              <p>
                Fifteen years of excellence in providing premium natural stone
                products to Australian homeowners, landscapers, and designers
                who demand the very best.
              </p>
            </div>

            <div className="highlight-item">
              <h4>Innovation in Design</h4>
              <p>
                We continuously research global landscape trends and work with
                designers to offer stone products that complement contemporary
                Australian outdoor living spaces.
              </p>
            </div>

            <div className="highlight-item">
              <h4>Customer Satisfaction</h4>
              <p>
                Our reputation is built on thousands of successful projects and
                satisfied customers who trust us to deliver exceptional
                materials and service every time.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="products" className="products-section">
        <div className="products-container">
          <div className="products-header">
            <h2>Our Premium Stone Collection</h2>
            <p>Handpicked natural materials for exceptional outdoor spaces</p>
          </div>
          <div className="products-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
